// ReviewDraft.jsx
import * as pdfjsLib from "pdfjs-dist";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.entry";
import { useEffect, useRef, useState } from "react";
import { Badge, Spinner } from "react-bootstrap";
import reviewerApi from "../../../api/reviewerApi";
import signerApi from "../../../api/signerapi";

// Set PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

// Strips a data URI prefix if present, e.g. "data:application/pdf;base64,XXXX" -> "XXXX"
const stripDataUriPrefix = (str) => {
  if (typeof str !== "string") return str;
  const commaIndex = str.indexOf("base64,");
  return commaIndex !== -1 ? str.slice(commaIndex + "base64,".length) : str;
};

// Shows the edited document that's currently out for review, rendered
// page-by-page via pdf.js, scaled to fit its container, alongside a
// "Document Status Log" side panel with reviewer + signer badges.
//
// Document source priority:
//   1. A reviewer row's `document` field (normal in-review case)
//   2. A signer's file, as a fallback when there are no reviewers /
//      no reviewer document yet (document moved straight to signing).
const ReviewDraft = ({ documentId, documentName }) => {
  const [reviewers, setReviewers] = useState([]);
  const [signers, setSigners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfRendered, setPdfRendered] = useState(false);
  const containerRef = useRef();

  useEffect(() => {
    if (!documentId) return;

    setLoading(true);
    setError(null);
    setPdfRendered(false); // reset so a new document actually re-renders

    Promise.allSettled([
      reviewerApi.getDetailsOfReviewerById(documentId),
      signerApi.getDetailsOfSignersById(documentId),
    ])
      .then(([reviewerResult, signerResult]) => {
        if (reviewerResult.status === "fulfilled") {
          const data = reviewerResult.value.data || [];
          console.log("[ReviewDraft] reviewers:", data);
          setReviewers(data);
        } else {
          console.error("Error fetching reviewers:", reviewerResult.reason);
          setReviewers([]);
        }

        if (signerResult.status === "fulfilled") {
          const data = signerResult.value.data || [];
          console.log("[ReviewDraft] signers:", data);
          setSigners(data);
        } else {
          console.error(
            "Error fetching signers (non-blocking):",
            signerResult.reason,
          );
          setSigners([]);
        }

        if (
          reviewerResult.status === "rejected" &&
          signerResult.status === "rejected"
        ) {
          setError("Failed to load the review draft.");
        }
      })
      .finally(() => setLoading(false));
  }, [documentId]);

  // Every reviewer row carries the same underlying document, so any
  // one of them is fine to render.
  const reviewerDocument = reviewers.find((r) => r.document)?.document || null;

  // Fallback: pull the file from a signer if no reviewer document is
  // available. Different backends have used different field names for
  // this over time, so check the common ones.
  const signerRecord = signers.find(
    (s) => s.signed_file || s.signedFile || s.file || s.document,
  );
  const signerDocument =
    signerRecord?.signed_file ||
    signerRecord?.signedFile ||
    signerRecord?.file ||
    signerRecord?.document ||
    null;

  const rawDocumentBase64 = reviewerDocument || signerDocument;
  const documentBase64 = stripDataUriPrefix(rawDocumentBase64);

  useEffect(() => {
    if (!documentBase64 || pdfRendered) return;

    const renderPdf = async () => {
      try {
        const byteCharacters = atob(documentBase64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);

        const loadingTask = pdfjsLib.getDocument({ data: byteArray });
        const pdf = await loadingTask.promise;

        const container = containerRef.current;
        if (!container) return;
        container.innerHTML = "";

        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const viewport = page.getViewport({ scale: 1.5 });

          const canvas = document.createElement("canvas");
          const context = canvas.getContext("2d");
          canvas.width = viewport.width;
          canvas.height = viewport.height;

          await page.render({ canvasContext: context, viewport }).promise;

          const img = document.createElement("img");
          img.src = canvas.toDataURL();
          img.style.width = "100%";
          img.style.height = "auto";
          img.style.display = "block";

          const wrapper = document.createElement("div");
          wrapper.style.width = "100%";
          wrapper.style.maxWidth = `${viewport.width}px`;
          wrapper.style.margin = "0 auto 12px auto";

          wrapper.appendChild(img);
          container.appendChild(wrapper);
        }

        setPdfRendered(true);
      } catch (err) {
        console.error("Error rendering PDF:", err);
        setError("Failed to render the document.");
      }
    };

    renderPdf();
  }, [documentBase64, pdfRendered]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center">
        <Spinner animation="border" />
      </div>
    );
  }

  if (error) {
    return <div className="text-center text-danger">{error}</div>;
  }

  const reviewedCount = reviewers.filter(
    (r) => r.approvedAt && r.approvedAt !== "N/A",
  ).length;
  const approvedCount = reviewers.filter((r) => r.approved).length;

  const signedCount = signers.filter(
    (s) => s.signStatus && s.signStatus.toLowerCase() === "completed",
  ).length;

  return (
    <div className="mt-3">
      <br />
      <h4>
        <strong>Review Draft - {documentName}</strong>
      </h4>

      <div style={{ display: "flex", gap: "16px" }}>
        <div
          style={{
            flex: 2,
            minWidth: 0,
            border: "1px solid #ddd",
            padding: "10px",
            backgroundColor: "#fff",
            maxHeight: "80vh",
            overflowY: "auto",
          }}
        >
          {documentBase64 ? (
            <div
              ref={containerRef}
              style={{
                background: "white",
                minHeight: "50vh",
                position: "relative",
              }}
            />
          ) : (
            <div className="text-muted text-center p-4">
              Document preview not available yet.
            </div>
          )}
        </div>

        <div
          style={{
            flex: 1,
            minWidth: 0,
            border: "1px solid #ddd",
            padding: "10px",
            backgroundColor: "#fff",
            maxHeight: "80vh",
            overflowY: "auto",
          }}
        >
          <h4 className="mb-3">Document Status Log</h4>

          <div className="mb-4">
            <div className="text-muted small mb-2">Reviewers</div>
            {reviewers.length === 0 ? (
              <div className="text-muted small">No reviewers found</div>
            ) : (
              <p className="mt-2 mb-0">
                <Badge bg="secondary" className="me-2">
                  {reviewedCount} / {reviewers.length} reviewed
                </Badge>
                <Badge
                  bg={
                    approvedCount === reviewers.length && reviewers.length > 0
                      ? "success"
                      : "warning"
                  }
                >
                  {approvedCount} / {reviewers.length} approved
                </Badge>
              </p>
            )}
          </div>

          <div>
            <div className="text-muted small mb-2">Signers</div>
            {signers.length === 0 || signedCount === 0 ? (
              <Badge bg="secondary">Awaiting signing</Badge>
            ) : (
              <Badge
                bg={signedCount === signers.length ? "success" : "warning"}
              >
                {signedCount} / {signers.length} signed
              </Badge>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewDraft;